import { useEffect, useState } from 'react'
import { EyeIcon, HeartIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline'
import { blogService } from '@/services/blogService'
import type { BlogPost } from '@/types/index'

const formatNumber = (num: number) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  return num.toString()
}

export const FeaturedBlogPosts = () => {
  const [featuredPosts, setFeaturedPosts] = useState<BlogPost[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    async function loadFeaturedPosts() {
      try {
        const posts = await blogService.getFeaturedPosts()
        setFeaturedPosts(posts.slice(0, 3))
      }
      catch {
        setFeaturedPosts([])
      }
      finally {
        setIsLoading(false)
      }
    }
    loadFeaturedPosts()
  }, [])

  if (isLoading) {
    return (
      <section className="mt-20">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Öne Çıkan Rehberler</h2>
          <p className="text-gray-600">Sınav tüyoları ve eğitim haberleri</p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="h-48 bg-gray-200 animate-pulse"></div>
              <div className="p-6">
                <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-full mb-1"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              </div>
            </div>
          ))}
        </div>
      </section>
    )
  }

  if (featuredPosts.length === 0) {
    return null
  }

  return (
    <section className="mt-20">
      <div className="text-center mb-10">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Öne Çıkan Rehberler</h2>
        <p className="text-gray-600">Sınav tüyoları ve eğitim haberleri</p>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {featuredPosts.map((post) => (
          <a
            key={post.id}
            href={`/blog/${post.slug}`}
            className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow group"
          >
            {post.featured_image ? (
              <div className="h-48 bg-gray-200 overflow-hidden">
                <img
                  src={post.featured_image}
                  alt={post.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
            ) : (
              <div className="h-48 bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
                <span className="text-4xl">📝</span>
              </div>
            )}
            <div className="p-6">
              {post.category && (
                <span className="text-xs text-blue-600 font-medium">
                  {post.category}
                </span>
              )}
              <h3 className="text-lg font-semibold mt-2 mb-2 text-gray-900 group-hover:text-blue-600 transition-colors">
                {post.name}
              </h3>
              {post.excerpt && (
                <p className="text-gray-600 text-sm line-clamp-3">
                  {post.excerpt}
                </p>
              )}
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>
                    {new Date(post.created_at).toLocaleDateString('tr-TR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                  {post.reading_time_minutes && (
                    <span>{post.reading_time_minutes} dakika okuma</span>
                  )}
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1" title="Görüntülenme">
                    <EyeIcon className="w-4 h-4" />
                    {formatNumber(post.view_count)}
                  </span>
                  <span className="inline-flex items-center gap-1" title="Beğeni">
                    <HeartIcon className="w-4 h-4" />
                    {formatNumber(post.like_count)}
                  </span>
                  <span className="inline-flex items-center gap-1" title="Yorum">
                    <ChatBubbleLeftRightIcon className="w-4 h-4" />
                    {formatNumber(post.comment_count)}
                  </span>
                </div>
              </div>
            </div>
          </a>
        ))}
      </div>

      <div className="text-center mt-10">
        <a
          href="/blog"
          className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
        >
          Tüm Blog Yazılarını Gör
        </a>
      </div>
    </section>
  )
}
